import { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';

import { FilledBadge } from 'components/shared/Badge';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { InputStyles } from 'components/shared/form/Input';
import Menu from 'components/shared/menu';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import ThinSearch from 'components/shared/ThinSearch';
import Workflow, { WorkflowAction, WorkflowLookupOption } from 'models/Workflow';
import { WorkflowActionIntent } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import BadgeCheckIcon from 'svg/BadgeCheckIcon';
import BadgeXIcon from 'svg/BadgeXIcon';
import CheckboxIndeterminateIcon from 'svg/CheckboxIndeterminateIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import FacilityCheckboxCheckedIcon from 'svg/FacilityCheckboxCheckedIcon';
import FacilityCheckboxIcon from 'svg/FacilityCheckboxIcon';

import { RowButton } from './categoryCard/styles';
import { buildingName, completionAttribution, sortedByBuildingName } from './FanoutPanel';
import { actionStatusDisplay } from './StatusBadge';

type Decision = 'accepted' | 'denied';

const DECISION_DISPLAY: Record<Decision, { chip: string; row: string; bulk: string; badge: 'green' | 'red' }> = {
  accepted: { chip: 'Accepted', row: 'Accept', bulk: 'Accept All', badge: 'green' },
  denied: { chip: 'Denied', row: 'Deny', bulk: 'Deny All', badge: 'red' },
};

// The deny modal's two dropdowns (OLIO-2728). UX renamed the labels on 2026-08-03; the API value
// names are unchanged, so the labels read inverted against them and that mapping is worth knowing:
//
//   "Denial Type" (required) -> denial_reason      — the fixed cross-client reporting rollup
//   "Reason"      (required) -> denial_description — an option from the client's lookup
//   "Notes"       (optional) -> facility_decision_note
//
// The value names stayed put deliberately: values are append-only and denials are already recorded
// under these names, so renaming them would orphan that history.
export const DENIAL_REASONS_LOOKUP = 'facility_denial_reasons';
const DENIAL_CATEGORIES = [
  { value: 'clinical', label: 'Clinical' },
  { value: 'financial', label: 'Financial' },
  { value: 'other', label: 'Other' },
];
const denialCategoryLabel = (value: string | undefined) =>
  DENIAL_CATEGORIES.find((category) => category.value === value)?.label ?? value;

// A checkbox scoped to this panel (not the shared components/shared/form/Checkbox, which backs
// checkboxes across the rest of the app) — the rounder, thinner-stroke mark this panel's design
// calls for. `data-cy='checkbox'` matches the shared component's own attribute so existing test
// helpers built against it keep working. Indeterminate (select-all with a partial selection) has
// no provided asset, so it falls back to the shared indeterminate glyph in the same blue.
function FanoutCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  'data-testid': dataTestId,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  'data-testid'?: string;
}) {
  return (
    <CheckboxButton
      type='button'
      role='checkbox'
      aria-checked={indeterminate ? 'mixed' : checked}
      disabled={disabled}
      onClick={onChange}
      data-cy='checkbox'
      data-testid={dataTestId}>
      {indeterminate ? (
        <CheckboxIndeterminateIcon width={12} height={12} color='#1283DA' />
      ) : checked ? (
        <FacilityCheckboxCheckedIcon />
      ) : (
        <FacilityCheckboxIcon />
      )}
    </CheckboxButton>
  );
}

// The Facility Decision(s) panel (OLIO-2611, Figma "Facility Decision(s)"): the one
// fanned-out step with search, select-all, and bulk Accept/Deny. Selection mechanics
// are deliberately THIS kind's, not the generic FanoutPanel's — no other per-building
// step has them (yet). Accepting or denying (single row or bulk) confirms through a
// modal with optional notes. A decision — one row's or the whole selection's — is ONE
// collection request, so the domain records the gesture once rather than leaving N
// completions to be reassembled afterward.
interface Props {
  actions: WorkflowAction[];
  workflow: Workflow;
  renderRow: (action: WorkflowAction) => ReactNode;
  // Every selected building in one request; a single row's decision is the one-element case.
  onComplete: (actionIds: string[], outputs: Record<string, string>) => unknown;
  // Decided rows amend through the same note modal (change decision / add note) or
  // revert to pending — see the row's overflow menu.
  onAmend: (actionId: string, outputs: Record<string, string>) => unknown;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isCompleting: boolean;
}

// outputFor is `unknown` (outputs are free-form jsonb) — narrow to a non-empty string.
const stringOutput = (action: WorkflowAction, name: string): string | undefined => {
  const value = action.outputFor(name);
  return typeof value === 'string' && value ? value : undefined;
};

// What the confirmation modal will do on confirm: first-time decisions complete; menu
// actions on decided rows amend the record. `mode` distinguishes the decided-row Add/Edit
// note gesture from a decision, which only changes the modal's copy — the DECISION picks
// the modal: every denial routes through the reason modal, notes included, because the API
// rejects a `denied` write with no denial_reason.
interface PendingConfirmation {
  decision: Decision;
  actionIds: string[];
  intent: 'complete' | 'amend';
  mode: 'decision' | 'note';
  title?: string;
  confirmLabel?: string;
  // Prefill when editing an existing note — the person edits what's there, never
  // blind-overwrites it.
  initialNote?: string;
  // Prefill the denial pair when amending an already-denied row. The API requires a
  // denial_reason on EVERY write that lands facility_determination: denied, and an amend
  // replaces the outputs wholesale — so a note edit has to resend the existing pair or it
  // would 422 (and, without the guard, erase the recorded reason).
  initialReason?: string;
  initialDescription?: string;
}

export default function FacilityDecisionPanel({
  actions,
  workflow,
  renderRow,
  onComplete,
  onAmend,
  onInvoke,
  isCompleting,
}: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The decision awaiting its confirmation modal — a row's Accept/Deny, a bulk one,
  // or a decided-row amendment from the overflow menu.
  const [confirming, setConfirming] = useState<PendingConfirmation | null>(null);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const named = sortedByBuildingName(actions).map((action) => ({
      action,
      name: buildingName(action),
    }));
    return query ? named.filter((row) => row.name.toLowerCase().includes(query)) : named;
  }, [actions, search]);

  // On a closed referral a building may still CHANGE ITS DECISION (OLIO-3170): that amend reopens the
  // referral, and if it makes every facility denied the outcome becomes Denied — over a cascaded
  // Denied being taken back, and over a person's Selected / Not Selected alike. So decided rows keep
  // their menu on a closed referral, reduced to that one item: "Revert to pending" is a rerun the API
  // ignores on a closed run, and a note edit re-sends the same decision, which would reopen the
  // referral just to attach words. Both stay off (`closed`), as do the bulk gestures and the pending
  // row's Accept/Deny, which only ever complete undecided rows and a closed referral has none.
  const closed = workflow.isTerminal;

  // Nothing to select or search for when only one facility is in play — the select-all
  // checkbox and search bar exist to manage a list, and a list of one has nothing to filter.
  const singleFacility = actions.length === 1;

  // Selection pruned to CURRENT action ids — rerun/amend retire an id and mint a
  // replacement, so a stored id can go stale between polls; a ghost id must not inflate
  // the count or be PATCHed (the API 404s retired ids).
  const currentIds = useMemo(() => new Set(actions.map((action) => action.id)), [actions]);
  const liveSelected = useMemo(() => new Set([...selected].filter((id) => currentIds.has(id))), [selected, currentIds]);

  // Bulk applies to EVERY selected actionable row, not just the ones the search filter
  // currently shows — the bar's count and the bulk action always agree.
  const actionable = actions.filter((action) => liveSelected.has(action.id) && action.isActionRequired);
  // Selection is a pending-decision affordance only (OLIO-2728): decided rows have no
  // checkbox and select-all never touches them.
  const selectableRows = rows.filter((row) => row.action.isActionRequired);
  const allVisibleSelected =
    selectableRows.length > 0 && selectableRows.every((row) => liveSelected.has(row.action.id));

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select-all operates on the VISIBLE pending rows only, but preserves selections the
  // search is currently hiding — union/subtract, never replace.
  const toggleAll = () => {
    setSelected(() => {
      const next = new Set(liveSelected);
      if (allVisibleSelected) selectableRows.forEach((row) => next.delete(row.action.id));
      else selectableRows.forEach((row) => next.add(row.action.id));
      return next;
    });
  };

  // One request either way, so there is no out-of-order settled snapshot to defend against.
  // Errors toast via the mutation's onError; a rejected batch applies nothing server-side.
  const confirm = async (extraOutputs: Record<string, string>) => {
    if (!confirming) return;
    const outputs: Record<string, string> = { facility_determination: confirming.decision, ...extraOutputs };
    const { intent } = confirming;
    // Re-prune at confirm: the modal can sit open across a nudge or the fallback poll, and a
    // retired id now fails the whole request rather than just itself.
    const actionIds = confirming.actionIds.filter((actionId) => currentIds.has(actionId));
    setConfirming(null);
    if (!actionIds.length) return;
    // Amend is a decided row's own gesture and is never bulk — it stays on the member route.
    const applied = intent === 'amend' ? onAmend(actionIds[0], outputs) : onComplete(actionIds, outputs);
    // Selection survives a rejected batch — nothing applied server-side, so the person
    // shouldn't have to rebuild a twenty-building selection to retry.
    await Promise.resolve(applied)
      .then(() => setSelected(new Set()))
      .catch(() => undefined);
  };

  const confirmWithNote = (note: string) => confirm(note ? { facility_decision_note: note } : {});

  const decisionFor = (action: WorkflowAction): Decision | null => {
    const value = workflow.valueFor('facility_determination', action)?.value;
    return value === 'accepted' || value === 'denied' ? value : null;
  };

  return (
    <Panel data-testid='facility-decision-panel'>
      {!singleFacility && (
        <Toolbar>
          {!closed && (
            <FanoutCheckbox
              checked={allVisibleSelected}
              indeterminate={liveSelected.size > 0 && !allVisibleSelected}
              onChange={toggleAll}
              data-testid='fanout-select-all'
            />
          )}
          <SearchWrap data-testid='fanout-search'>
            <ThinSearch value={search} onChange={setSearch} placeholder='Search for Facility by Name' />
          </SearchWrap>
        </Toolbar>
      )}

      {!closed && liveSelected.size > 0 && (
        <SelectionBar data-testid='fanout-selection-bar'>
          <BodySmallBold>
            {liveSelected.size} {liveSelected.size === 1 ? 'Facility' : 'Facilities'} Selected
          </BodySmallBold>
          {actionable.length > 0 && (
            <>
              <BarDivider />
              {(['accepted', 'denied'] as Decision[]).map((decision) => (
                <BulkLink
                  key={decision}
                  type='button'
                  $color={decision === 'denied' ? colors.red100 : colors.green100}
                  disabled={isCompleting}
                  onClick={() =>
                    setConfirming({
                      decision,
                      actionIds: actionable.map((action) => action.id),
                      intent: 'complete',
                      mode: 'decision',
                    })
                  }
                  data-testid={`fanout-bulk-${DECISION_DISPLAY[decision].row}`}>
                  {DECISION_DISPLAY[decision].bulk}
                </BulkLink>
              ))}
            </>
          )}
        </SelectionBar>
      )}

      {rows.map(({ action, name }) => {
        const decision = action.isCompleted ? decisionFor(action) : null;
        const note = decision ? workflow.valueFor('facility_decision_note', action)?.value : null;

        return (
          <Row key={action.id} data-testid='fanout-row' data-group-id={action.groupId ?? undefined}>
            {!closed && (
              // Decided rows keep their checkbox slot rather than losing it — unchecked and
              // dimmed (disabled → 25% opacity) so every row's content lines up at the same left
              // edge regardless of decision state. Disabled also makes it inert to a click, and
              // selectableRows already excludes it from select-all's target set.
              <FanoutCheckbox
                checked={liveSelected.has(action.id)}
                onChange={() => toggle(action.id)}
                disabled={!action.isActionRequired}
              />
            )}
            <RowMain>
              <RowHeader>
                <RowName>{name}</RowName>
                {decision && (
                  <FilledBadge
                    color={DECISION_DISPLAY[decision].badge}
                    icon={DECISION_DISPLAY[decision].badge === 'green' ? <BadgeCheckIcon /> : <BadgeXIcon />}
                    data-testid='decision-chip'>
                    {DECISION_DISPLAY[decision].chip}
                  </FilledBadge>
                )}
              </RowHeader>
              <RowSubtitleLine action={action} decision={decision} />
              {decision === 'denied' && <DenialReasonLine action={action} workflow={workflow} />}
              {decision && note && <RowNote data-testid='decision-note-display'>&ldquo;{note}&rdquo;</RowNote>}
              {renderRow(action)}
            </RowMain>
            {decision && (
              <RowActions>
                <Menu $size='small'>
                  <Menu.Trigger>
                    <MenuButton type='button' aria-label='Decision options' data-testid='decision-menu'>
                      <EllipsisIcon />
                    </MenuButton>
                  </Menu.Trigger>
                  <Menu.Content position='right'>
                    <Menu.Item
                      data-testid='menu-change-decision'
                      onClick={() =>
                        setConfirming({
                          decision: decision === 'accepted' ? 'denied' : 'accepted',
                          actionIds: [action.id],
                          intent: 'amend',
                          mode: 'decision',
                          // Carry the existing note forward — the amend's reset retires
                          // it, and a blank modal would silently drop it otherwise.
                          initialNote: typeof note === 'string' ? note : undefined,
                        })
                      }>
                      Change to {decision === 'accepted' ? 'denied' : 'accepted'}
                    </Menu.Item>
                    {!closed && (
                      <>
                        <Menu.Item data-testid='menu-revert-pending' onClick={() => onInvoke(action.id, 'rerun')}>
                          Revert to pending
                        </Menu.Item>
                        <Menu.Item
                          data-testid='menu-add-note'
                          onClick={() =>
                            setConfirming({
                              decision,
                              actionIds: [action.id],
                              intent: 'amend',
                              mode: 'note',
                              title: note ? 'Edit Note(s)' : 'Add Note(s)',
                              confirmLabel: 'Save Note',
                              initialNote: typeof note === 'string' ? note : undefined,
                              // Carry the recorded denial pair into the modal so the amend resends
                              // it — see PendingConfirmation.initialReason.
                              initialReason: stringOutput(action, 'denial_reason'),
                              initialDescription: stringOutput(action, 'denial_description'),
                            })
                          }>
                          {note ? 'Edit note' : 'Add note'}
                        </Menu.Item>
                      </>
                    )}
                  </Menu.Content>
                </Menu>
              </RowActions>
            )}
            {action.isActionRequired && !closed && (
              <RowActions>
                {(['accepted', 'denied'] as Decision[]).map((decision) => (
                  <RowButton
                    key={decision}
                    variant={decision === 'denied' ? 'outline-danger' : 'outline-green'}
                    disabled={isCompleting}
                    onClick={() =>
                      setConfirming({ decision, actionIds: [action.id], intent: 'complete', mode: 'decision' })
                    }
                    data-testid={`action-option-${DECISION_DISPLAY[decision].row}`}>
                    {DECISION_DISPLAY[decision].row}
                  </RowButton>
                ))}
              </RowActions>
            )}
          </Row>
        );
      })}
      {rows.length === 0 && <Empty>No facilities match your search.</Empty>}

      {confirming &&
        // Every denial goes through the reason modal, note edits included: the API rejects a
        // `denied` write with no denial_reason, so the plain note modal made Edit note 422 on a
        // denied row (OLIO-2728 QA). Editing the note is also the only way to correct a
        // mis-picked reason, which is what QA asked for.
        (confirming.decision === 'denied' ? (
          <DenyReasonModal
            workflow={workflow}
            facilityCount={confirming.actionIds.length}
            title={confirming.title}
            confirmLabel={confirming.confirmLabel}
            initialNote={confirming.initialNote}
            initialReason={confirming.initialReason}
            initialDescription={confirming.initialDescription}
            isCompleting={isCompleting}
            onCancel={() => setConfirming(null)}
            onConfirm={confirm}
          />
        ) : (
          <DecisionNoteModal
            decision={confirming.decision}
            title={confirming.title}
            confirmLabel={confirming.confirmLabel}
            initialNote={confirming.initialNote}
            isCompleting={isCompleting}
            onCancel={() => setConfirming(null)}
            onConfirm={confirmWithNote}
          />
        ))}
    </Panel>
  );
}

// The muted line under the facility name: "Pending Decision" while actionable,
// completion attribution once decided, otherwise the plain status.
function RowSubtitleLine({ action, decision }: { action: WorkflowAction; decision: Decision | null }) {
  if (decision) {
    const attribution = completionAttribution(action);
    return attribution ? <RowSubtitle>{attribution}</RowSubtitle> : null;
  }
  return (
    <RowSubtitle>
      {action.isActionRequired ? 'Pending Decision' : actionStatusDisplay(action.effectiveStatus).label}
    </RowSubtitle>
  );
}

// The Accept/Deny confirmation (Figma "Add Note(s)"): the decision goes through with an optional
// note that lands on each building's completion. Same field and same label as the deny modal's
// Notes — it's the one `facility_decision_note`, so the two must not drift apart.
function DecisionNoteModal({
  decision,
  title: titleOverride,
  confirmLabel,
  initialNote,
  isCompleting,
  onCancel,
  onConfirm,
}: {
  decision: Decision;
  title?: string;
  confirmLabel?: string;
  initialNote?: string;
  isCompleting: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState(initialNote ?? '');
  const title = titleOverride ?? (decision === 'accepted' ? 'Accept Referral' : 'Deny Referral');

  return (
    <StandardModal
      title={title}
      onCancel={onCancel}
      colorTheme={decision === 'denied' ? ColorThemeName.PrimaryRed : ColorThemeName.Primary}>
      <ModalContent>
        <BodySmall>Provide any notes below explaining your reasoning:</BodySmall>
        <div>
          <FieldLabel name='decision-note'>Notes (optional)</FieldLabel>
          <NoteInput
            id='decision-note'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='Type here to add a note...'
            data-testid='decision-note'
          />
        </div>
      </ModalContent>
      <ModalButtons>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant={decision === 'denied' ? 'danger' : 'primary'}
          disabled={isCompleting}
          onClick={() => onConfirm(note.trim())}
          data-testid='decision-confirm'>
          {confirmLabel ?? title}
        </Button>
      </ModalButtons>
    </StandardModal>
  );
}

// "Clinical: No Skilled Need" — the recorded pair, read from the completed action's outputs
// (slugs) and labeled through the workflow's lookup so client wording renders. Reads in the
// renamed vocabulary: the denial type, then its reason. Denials recorded before this existed have
// no denial_reason output and show nothing; ones recorded while the reason was still optional show
// the type alone.
function DenialReasonLine({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const reason = action.outputFor('denial_reason');
  if (typeof reason !== 'string' || !reason) return null;

  const description = action.outputFor('denial_description');
  const descriptionLabel =
    typeof description === 'string' && description
      ? (workflow.lookupLabel(DENIAL_REASONS_LOOKUP, description) ?? description)
      : null;

  return (
    <RowNote data-testid='denial-reason-display'>
      Denied — {denialCategoryLabel(reason)}
      {descriptionLabel ? `: ${descriptionLabel}` : ''}
    </RowNote>
  );
}

// The Deny Referral modal (OLIO-2728, Figma "Deny Referral"). Denial Type (the fixed category) and
// Reason (the client's configured options within that type, from the workflow's lookups payload)
// are BOTH required — UX confirmed that on 2026-08-03, closing the open question this modal
// shipped with. A bulk denial states how many facilities it reaches. Notes stay optional, keeping
// parity with the accept flow (and carrying an existing note through a change-to-denied amendment).
function DenyReasonModal({
  workflow,
  facilityCount,
  title,
  confirmLabel,
  initialNote,
  initialReason,
  initialDescription,
  isCompleting,
  onCancel,
  onConfirm,
}: {
  workflow: Workflow;
  facilityCount: number;
  title?: string;
  confirmLabel?: string;
  initialNote?: string;
  initialReason?: string;
  initialDescription?: string;
  isCompleting: boolean;
  onCancel: () => void;
  onConfirm: (outputs: Record<string, string>) => void;
}) {
  const allDescriptions = workflow.lookupOptions(DENIAL_REASONS_LOOKUP);
  // Only offer a Denial Type that actually has reasons to pick. Now that a reason is required, a
  // type with nothing under it would be a dead end — the person picks it and can't deny at all.
  // The global list covers all three, but a client's own list may not (lookups are client-over-
  // global), so derive the offered types from the list in hand.
  const denialTypes = DENIAL_CATEGORIES.filter((category) =>
    allDescriptions.some((option) => option.category === category.value)
  );
  const [reason, setReason] = useState(initialReason ?? '');
  const [description, setDescription] = useState<WorkflowLookupOption | null>(
    // Resolve the recorded slug against the client's list. An option that has since been
    // archived out of the list won't resolve — the field opens empty rather than showing a
    // slug the person can't re-pick.
    () => allDescriptions.find((option) => option.apiName === initialDescription) ?? null
  );
  const [note, setNote] = useState(initialNote ?? '');
  // Amending an already-denied row: the decision stands, so don't re-ask "are you sure".
  const amending = Boolean(initialReason);
  const descriptionOptions = allDescriptions.filter((option) => option.category === reason);

  const submit = () => {
    // Guard mirrors the disabled confirm button — both are required, so there's no partial write.
    if (!reason || !description) return;

    const outputs: Record<string, string> = { denial_reason: reason, denial_description: description.apiName };
    if (note.trim()) outputs.facility_decision_note = note.trim();
    onConfirm(outputs);
  };

  return (
    <StandardModal title={title ?? 'Deny Referral'} onCancel={onCancel} colorTheme={ColorThemeName.PrimaryRed}>
      <ModalContent>
        <BodySmall>
          {amending ? (
            'Update the denial type, reason, or notes for this denial.'
          ) : (
            <>
              <BodySmallBold as='span'>Are you sure you wish to deny this referral?</BodySmallBold> A denial type and
              reason must be provided below for denying a referral.
            </>
          )}
        </BodySmall>
        {facilityCount > 1 && (
          <BodySmall data-testid='deny-bulk-count'>
            Denial will be applied to {facilityCount} selected facilities.
          </BodySmall>
        )}
        <div data-testid='denial-reason-select'>
          <FieldLabel name='denial-reason'>Denial Type</FieldLabel>
          <Select
            inputId='denial-reason'
            placeholder='Denial Type'
            options={denialTypes}
            value={denialTypes.find((category) => category.value === reason) ?? null}
            onChange={(option) => {
              setReason(option?.value ?? '');
              setDescription(null);
            }}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
        <div data-testid='denial-description-select'>
          <FieldLabel name='denial-description'>Reason</FieldLabel>
          <Select
            inputId='denial-description'
            placeholder='Reason'
            options={descriptionOptions}
            value={description}
            isDisabled={!reason}
            onChange={(option) => setDescription(option ?? null)}
            getOptionValue={(option: WorkflowLookupOption) => option.apiName}
            getOptionLabel={(option: WorkflowLookupOption) => option.displayName}
          />
        </div>
        <div>
          <FieldLabel name='decision-note'>Notes (optional)</FieldLabel>
          <NoteInput
            id='decision-note'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='Type here to add a note...'
            data-testid='decision-note'
          />
        </div>
      </ModalContent>
      <ModalButtons>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        {/* Both dropdowns are required (UX, 2026-08-03) — the API enforces the same pair, so a
            bulk denial can't slip through with only a type either. */}
        <Button
          variant='danger'
          disabled={isCompleting || !reason || !description}
          onClick={submit}
          data-testid='decision-confirm'>
          {confirmLabel ?? 'Deny Referral'}
        </Button>
      </ModalButtons>
    </StandardModal>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.25;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.black5};
`;

const SearchWrap = styled.div`
  flex: 1;
  max-width: 360px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: ${colors.black3};
  border-bottom: 1px solid ${colors.black5};
`;

const BarDivider = styled.span`
  width: 1px;
  align-self: stretch;
  background: ${colors.black10};
`;

const BulkLink = styled.button<{ $color: string }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  background: ${colors.white};
  cursor: pointer;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const RowName = styled(BodySmallBold)`
  font-size: 14px;
`;

const RowSubtitle = styled(BodySmall)`
  color: ${colors.black50};
`;

const RowNote = styled(BodySmall)`
  color: ${colors.black100};
`;

const Empty = styled(BodySmall)`
  padding: 16px;
  color: ${colors.black50};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const NoteInput = styled.textarea`
  ${InputStyles}
  min-height: 96px;
  resize: vertical;
`;

const ModalButtons = styled(ButtonGroup)`
  justify-content: flex-end;
`;
